import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowDown, BadgeCheck } from "lucide-react";
import { useSmoothScroll } from "../context/ScrollContext";

const HERO_BG =
  "https://images.unsplash.com/photo-1543342384-1f1350e27861?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwbW90aGVyJTIwbmV3Ym9ybiUyMGJhYnklMjBjYXJlJTIwbWF0ZXJuYWwlMjBoZWFsdGglMjBkb3VsYSUyMGNvYWNoaW5nfGVufDB8fHx8MTc5MjAwMjYyMnww&ixlib=rb-4.1.0&q=85";

const lines = ["Modern Doula Care.", "For Every Family."];

const trustBadges = [
  "DONA-Trained Doula",
  "Certified Health Coach",
  "San Joaquin County Local",
];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.16, duration: 1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HeroSection = () => {
  const { scrollTo } = useSmoothScroll();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0 scale-110">
        <img
          src={HERO_BG}
          alt="Mother with newborn in maternal care"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FBFAF7] via-[#FBFAF7]/80 to-[#FBFAF7]/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FBFAF7] via-transparent to-[#FBFAF7]/40" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full pt-32 pb-24"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-san-joaquin-badge"
          className="inline-flex items-center gap-2 rounded-md border-2 border-[#2B1B45] bg-white/70 backdrop-blur px-4 py-1.5 mb-8"
        >
          <MapPin className="h-3.5 w-3.5 text-[#2563EB]" />
          <span className="text-xs uppercase tracking-[0.22em] font-heading font-bold text-[#2B1B45]">
            Serving San Joaquin County, CA
          </span>
        </motion.div>

        <p className="text-xs uppercase tracking-[0.25em] font-heading font-bold text-[#F2644C] mb-5">
          Full-Spectrum Doula Care &amp; Health Coaching
        </p>

        <h1
          data-testid="hero-headline"
          className="font-heading font-extrabold text-[#2B1B45] text-5xl sm:text-7xl lg:text-8xl leading-[0.98] tracking-tight"
        >
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1 -mb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i === 1 ? "font-serif italic font-semibold text-[#6D28D9]" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subhead"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-base sm:text-lg text-[#5B5470] leading-relaxed"
        >
          Professional prenatal, birth, and postpartum support for families in
          San Joaquin County, led by Adonica Shaw, CBE, INHC, CIHC.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => scrollTo("#contact")}
            className="px-8 py-4 rounded-full bg-[#F2644C] text-white text-sm font-bold tracking-wide hover:bg-[#D9523B] transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-0.5"
          >
            Schedule consultation
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => scrollTo("#pricing")}
            className="px-8 py-4 rounded-full border-2 border-[#2B1B45] text-sm font-bold tracking-wide text-[#2B1B45] hover:bg-[#2B1B45] hover:text-white transition-all duration-300"
          >
            View service tiers
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.9 }}
          className="mt-12 flex flex-wrap gap-x-6 gap-y-2"
        >
          {trustBadges.map((b) => (
            <span
              key={b}
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#5B5470]"
            >
              <BadgeCheck className="h-4 w-4 text-[#2563EB]" />
              {b}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollTo("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute z-10 bottom-8 left-1/2 -translate-x-1/2 text-[#5B5470] hover:text-[#F2644C] transition-colors"
        aria-label="Scroll to services"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        >
          <ArrowDown className="h-5 w-5" />
        </motion.div>
      </motion.button>
    </section>
  );
};

export default HeroSection;
